import {
  createElement,
  Fragment,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { withTranslation } from "next-i18next";
import { createRoot } from "react-dom/client";
import { autocomplete } from "@algolia/autocomplete-js";
import { createLocalStorageRecentSearchesPlugin } from "@algolia/autocomplete-plugin-recent-searches";
import { createQuerySuggestionsPlugin } from "@algolia/autocomplete-plugin-query-suggestions";
import { useRouter } from "next/router";

// component made based on this:
// https://www.algolia.com/doc/ui-libraries/autocomplete/integrations/with-react-instantsearch-hooks/

function Autocomplete({
  submitSearch,
  searchClient,
  className,
  t,
  ...autocompleteProps
}) {
  const router = useRouter();
  const { locale } = router;

  const autocompleteContainer = useRef(null);
  const lastQuerySuggestionsResults = useRef([]);
  const lastQuery = useRef("");

  const [query, setQuery] = useState("");

  function debouncePromise(func, wait) {
    return function (params) {
      return new Promise((resolve) => {
        setTimeout(() => {
          if (params.state.query !== lastQuery.current) {
            return resolve(lastQuerySuggestionsResults.current);
          }

          resolve(func(params));
        }, wait);
      });
    };
  }

  const plugins = useMemo(() => {
    const recentSearches = createLocalStorageRecentSearchesPlugin({
      key: "instantsearch",
      limit: 3,
      transformSource({ source }) {
        return {
          ...source,
          onSelect({ item }) {
            setQuery(item.label);
            submitSearch(item.label);
          },
        };
      },
    });

    const querySuggestions = createQuerySuggestionsPlugin({
      searchClient,
      indexName: process.env.NEXT_PUBLIC_INSTANT_SEARCH_QUERY_SUGGESTIONS,
      getSearchParams() {
        return recentSearches.data.getAlgoliaSearchParams({
          hitsPerPage: 6,
        });
      },
      transformSource({ source }) {
        function delayedGetItems(params) {
          const newQuerySuggestionsResults = source.getItems(params);
          lastQuerySuggestionsResults.current = newQuerySuggestionsResults;

          return newQuerySuggestionsResults;
        }
        const debouncedGetItems = debouncePromise(delayedGetItems, 500);

        return {
          ...source,
          sourceId: "querySuggestionsPlugin",
          onSelect({ item }) {
            setQuery(item.query);
            submitSearch(item.query);
          },
          getItems(params) {
            if (!params.state.query) {
              return [];
            }

            lastQuery.current = params.state.query;

            return debouncedGetItems(params);
          },
        };
      },
    });

    return [recentSearches, querySuggestions];
  }, []);

  useEffect(() => {
    if (!autocompleteContainer.current) {
      return;
    }

    let root;

    const autocompleteInstance = autocomplete({
      ...autocompleteProps,
      container: autocompleteContainer.current,
      initialState: { query },
      onReset() {
        setQuery("");
      },
      onSubmit({ state }) {
        setQuery(state.query);
        submitSearch(state.query);
      },
      onStateChange({ prevState, state }) {
        if (prevState.query !== state.query) {
          setQuery(state.query);
        }
      },
      renderer: { createElement, Fragment, render: (element, container) => {
        if (!root) {
          root = createRoot(container);
        }
        root.render(element);
      }},
      detachedMediaQuery: 'none',
      placeholder: t("common:algolia.placeholder"),
      translations: {
        submitButtonTitle: t("common:algolia.start_search"),
        clearButtonTitle: t("common:algolia.delete"),
      },
      plugins,
    });

    // eslint-disable-next-line consistent-return
    return () => autocompleteInstance.destroy();
  }, [plugins, locale]);

  return (
    <div
      className={className}
      ref={autocompleteContainer}
    />
  );
}

export default withTranslation("common")(Autocomplete);
